import React, { createContext, useContext, useEffect } from 'react';
import { Config } from '@applied/proto/web_services/candidate_experience/proto/config_pb';
import type { JsonValue } from '@protobuf-ts/runtime';
import type { MessageType } from '@protobuf-ts/runtime';
import { useQuery } from '@tanstack/react-query';
import type { ResponseData } from 'api/http_requests';
import { getData } from 'api/http_requests';

import AppliedFaviconSvg from 'assets/images/applied/Applied_Intuition_Symbol_Favicon.svg';

const configType = Config as MessageType<Config>;

export type ConfigContextType = {
  isLoading: boolean;
  config: Config | undefined;
};

const ConfigContext = createContext<ConfigContextType | null>(null);

async function fetchConfig(): Promise<Config | undefined> {
  const response: ResponseData<JsonValue> = await getData('/api/config/');
  if (!response.jsonPayload) {
    throw new Error('Failed to fetch config');
  }
  return configType.fromJson(response.jsonPayload);
}

export function ConfigContextProvider({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element {
  const { data: config, isLoading } = useQuery({
    queryKey: ['config'],
    queryFn: fetchConfig,
  });

  return React.createElement(
    ConfigContext.Provider,
    { value: { isLoading, config: config ?? undefined } },
    children,
  );
}

export function useConfig(): ConfigContextType {
  const context = useContext(ConfigContext);
  if (!context) {
    return { isLoading: false, config: undefined };
  }
  return context;
}

export function FontToggle(): null {
  useEffect(function applyAppliedSansFontAndFavicon() {
    const root = document.querySelector('.candidate_experience-app');
    root?.classList.add('use-applied-sans');

    const links = document.querySelectorAll<HTMLLinkElement>(
      'link[rel="icon"], link[rel="shortcut icon"]',
    );
    for (const faviconLink of links) {
      faviconLink.href = AppliedFaviconSvg;
      faviconLink.type = 'image/svg+xml';
    }
  }, []);
  return null;
}
